import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  LinearProgress,
  Pagination,
  Grid,
  Avatar,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Snackbar,
} from '@mui/material';
import {
  History,
  Search,
  FilterList,
  Person,
  Edit,
  Add,
  Delete,
  Visibility,
  Payment,
  Block,
  CheckCircle,
  Refresh,
  Clear,
  Schedule,
  FilterAlt,
} from '@mui/icons-material';
import { logService } from '../../services/logService';
import { Log, ApiResponse } from '../../types';

interface ClientLogsProps {
  clientId: number;
}

const ClientLogs: React.FC<ClientLogsProps> = ({ clientId }) => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [startDateTime, setStartDateTime] = useState<string>('');
  const [endDateTime, setEndDateTime] = useState<string>('');
  
  // Estados para os filtros temporários (não aplicados ainda)
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const [tempActionFilter, setTempActionFilter] = useState('');
  const [tempStartDateTime, setTempStartDateTime] = useState<string>('');
  const [tempEndDateTime, setTempEndDateTime] = useState<string>('');
  
  // Estado para o toast de validação
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = { page, limit: 10 };
      
      // Adicionar filtros de data se fornecidos
      if (startDateTime) {
        params.startDate = startDateTime;
      }
      if (endDateTime) {
        params.endDate = endDateTime;
      }
      
      const response: ApiResponse<Log[]> = await logService.getLogsByEntity(
        'cliente',
        clientId,
        params
      );

      if (response.success && response.data) {
        setLogs(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.pages);
          setTotalLogs(response.pagination.total);
        }
      } else {
        setError(response.message || 'Erro ao carregar logs');
      }
    } catch (err) {
      setError('Erro ao carregar logs do cliente');
      console.error('Erro ao buscar logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchLogs();
    }
  }, [clientId, page, startDateTime, endDateTime]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleRefresh = () => {
    fetchLogs();
  };

  // Função para validar se a data/hora fim não é menor que a data/hora início
  const validateDateTimeRange = (startDate: string, endDate: string): boolean => {
    if (!startDate || !endDate) return true; // Se um dos campos estiver vazio, não há erro
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return end >= start;
  };

  const handleApplyFilters = () => {
    // Validar se a data/hora fim não é menor que a data/hora início
    if (!validateDateTimeRange(tempStartDateTime, tempEndDateTime)) {
      setToastMessage('A data/hora fim não pode ser menor que a data/hora início');
      setToastOpen(true);
      return;
    }
    
    setSearchTerm(tempSearchTerm);
    setActionFilter(tempActionFilter);
    setStartDateTime(tempStartDateTime);
    setEndDateTime(tempEndDateTime);
    setPage(1); // Reset para primeira página ao aplicar filtros
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setActionFilter('');
    setStartDateTime('');
    setEndDateTime('');
    setTempSearchTerm('');
    setTempActionFilter('');
    setTempStartDateTime('');
    setTempEndDateTime('');
    setPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionColor = (acao: string) => {
    const colors: Record<string, 'success' | 'warning' | 'error' | 'primary' | 'info'> = {
      criacao: 'success',
      edicao: 'warning',
      exclusao: 'error',
      pagamento: 'primary',
      cancelamento: 'error',
      autenticacao: 'info',
      logout: 'info',
      bloqueio: 'error',
      ativacao: 'success',
      desativacao: 'warning',
      visualizacao: 'info',
      outros: 'info'
    };
    return colors[acao] || 'info';
  };

  const getActionIcon = (acao: string) => {
    switch (acao) {
      case 'criacao':
        return <Add />;
      case 'edicao':
        return <Edit />;
      case 'ativacao':
        return <CheckCircle />;
      case 'bloqueio':
        return <Block />;
      case 'cancelamento':
        return <Delete />;
      case 'pagamento':
        return <Payment />;
      case 'visualizacao':
        return <Visibility />;
      default:
        return <History />;
    }
  };

  const formatAction = (acao: string) => {
    const actions: Record<string, string> = {
      criacao: 'Criação',
      edicao: 'Edição',
      exclusao: 'Exclusão',
      pagamento: 'Pagamento',
      cancelamento: 'Cancelamento',
      autenticacao: 'Autenticação',
      logout: 'Logout',
      bloqueio: 'Bloqueio',
      ativacao: 'Ativação',
      desativacao: 'Desativação',
      visualizacao: 'Visualização',
      outros: 'Outros'
    };
    return actions[acao] || acao;
  };

  const getProfileColor = (perfil: string) => {
    switch (perfil) {
      case 'administrador':
        return 'error';
      case 'financeiro':
        return 'warning';
      case 'suporte':
        return 'info';
      case 'cliente':
        return 'success';
      default:
        return 'default';
    }
  };

  // Filtrar logs baseado na busca e filtro de ação
  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm || 
      log.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.usuario?.nome.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAction = !actionFilter || log.acao === actionFilter;
    
    return matchesSearch && matchesAction;
  });

  const hasActiveFilters = searchTerm || actionFilter || startDateTime || endDateTime;
  const hasTempFilters = tempSearchTerm || tempActionFilter || tempStartDateTime || tempEndDateTime;

  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Carregando histórico...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
          Histórico de Atividades ({totalLogs})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Chip
            icon={<Refresh sx={{ fontSize: '0.9rem' }} />}
            label="Atualizar"
            onClick={handleRefresh}
            variant="outlined"
            size="small"
            sx={{ height: 24, fontSize: '0.75rem' }}
          />
        </Stack>
      </Box>      

      {/* Resumo */}
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center">
                <History color="primary" sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Total de Eventos
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                    {totalLogs}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center">
                <Edit color="info" sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Modificações
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                    {logs.filter(log => log.acao === 'edicao').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center">
                <Payment color="success" sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Pagamentos
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                    {logs.filter(log => log.acao === 'pagamento').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center">
                <Visibility color="inherit" sx={{ mr: 1, fontSize: '1.2rem' }} />
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    Visualizações
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                    {logs.filter(log => log.acao === 'visualizacao' as any).length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Grid container spacing={1} mb={1.5}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar no histórico..."
            value={tempSearchTerm}
            onChange={(e) => setTempSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: '1rem' }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Filtrar por ação</InputLabel>
            <Select
              value={tempActionFilter}
              onChange={(e) => setTempActionFilter(e.target.value)}
              label="Filtrar por ação"
              size="small"
              startAdornment={
                <InputAdornment position="start">
                  <FilterList sx={{ fontSize: '1rem' }} />
                </InputAdornment>
              }
            >
              <MenuItem value="">Todas as ações</MenuItem>
              <MenuItem value="criacao">Criação</MenuItem>
              <MenuItem value="edicao">Edição</MenuItem>
              <MenuItem value="ativacao">Ativação</MenuItem>
              <MenuItem value="bloqueio">Bloqueio</MenuItem>
              <MenuItem value="desbloqueio">Desbloqueio</MenuItem>
              <MenuItem value="cancelamento">Cancelamento</MenuItem>
              <MenuItem value="pagamento">Pagamento</MenuItem>
              <MenuItem value="visualizacao">Visualização</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            type="datetime-local"
            label="Data/Hora Início"
            value={tempStartDateTime}
            onChange={(e) => setTempStartDateTime(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Schedule sx={{ fontSize: '1rem' }} />
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            size="small"
            type="datetime-local"
            label="Data/Hora Fim"
            value={tempEndDateTime}
            onChange={(e) => setTempEndDateTime(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Schedule sx={{ fontSize: '1rem' }} />
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Stack direction="row" spacing={0.25}>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={!hasTempFilters}
              startIcon={<FilterAlt />}
              size="small"
              sx={{ minWidth: 'auto', px: 0.5 }}
            >
              Aplicar
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
              startIcon={<Clear />}
              size="small"
              sx={{ minWidth: 'auto', px: 0.5 }}
            >
              Limpar
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Timeline de Logs */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>ID</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Data/Hora</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Ação</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Descrição</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Usuário</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                  <History sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
                  <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    Nenhum log encontrado para este cliente
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ py: 0.5, px: 1.5 }}>
                    <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                      {log.id}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1.5 }}>
                    <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                      {formatDate(log.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1.5 }}>
                    <Chip
                      label={formatAction(log.acao)}
                      color={getActionColor(log.acao)}
                      size="small"
                      icon={getActionIcon(log.acao)}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {log.descricao}
                    </Typography>
                    {(log.dados_antes || log.dados_depois) && (
                      <Box mt={0.5}>
                        {log.dados_antes && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                            Antes: {JSON.stringify(log.dados_antes)}
                          </Typography>
                        )}
                        {log.dados_depois && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                            Depois: {JSON.stringify(log.dados_depois)}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1.5 }}>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ width: 20, height: 20, mr: 0.5 }}>
                        <Person sx={{ fontSize: '0.8rem' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                          {log.usuario?.nome || `Usuário ${log.usuario_id}`}
                        </Typography>
                        <Chip
                          label={log.usuario?.perfil}
                          color={getProfileColor(log.usuario?.perfil || '') as any}
                          size="small"
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.65rem' }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 1.5 }}>
                    <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                      {log.ip_address || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
            size="small"
          />
        </Box>
      )}

             {/* Informação sobre retenção de logs */}
       <Alert severity="info" sx={{ mt: 2, fontSize: '0.8rem' }}>
         <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
           <strong>Retenção de Logs:</strong> O histórico de atividades é mantido por 2 anos. 
           Logs mais antigos são arquivados automaticamente.
         </Typography>
       </Alert>
       
       {/* Toast para mensagens de validação */}
       <Snackbar
         open={toastOpen}
         autoHideDuration={4000}
         onClose={() => setToastOpen(false)}
         anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
       >
         <Alert 
           onClose={() => setToastOpen(false)} 
           severity="error" 
           sx={{ width: '100%' }}
         >
           {toastMessage}
         </Alert>
       </Snackbar>
     </Box>
   );
 };

export default ClientLogs; 